import { Body1Strong, Button, Spinner, Tab, TabList, Tooltip } from '@fluentui/react-components'
import { AddRegular, DismissCircle48Color } from '@fluentui/react-icons'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '../../components/Dialog/Dialog'
import { EmptyPromptsContent } from '../../components/EmptyPromptsContent/EmptyPromptsContent'
import { PageHeader } from '../../components/PageHeader/PageHeader'
import { PromptCard } from '../../components/PromptCard/PromptCard'
import { StandardPromptAccordion } from '../../components/StandardPromptAccordion/StandardPromptAccordion'
import { useGetPrompts } from '../../hooks/useGetPrompts'
import {
  isPromptTabValue,
  PromptTabValue,
  resolveInitialPromptTab,
  savePromptTab,
} from './prompt-tab-storage'
import type { PromptTabValue as TabValue } from './prompt-tab-storage'
import { useStyles } from './Prompts.styles'
import { useImportPrompts } from './useImportPrompts'

const Prompts = () => {
  const [selectedTab, setSelectedTab] = useState<TabValue | null>(null)
  const classes = useStyles()
  const { t } = useTranslation()
  const importInputRef = useRef<HTMLInputElement | null>(null)

  const { data, isLoading, isError, isFetching, refetch } = useGetPrompts()
  const { importPrompts, cancelImportPrompts, isImportingPrompts } = useImportPrompts({ refetch })

  const handleOpenImportPicker = useCallback(() => {
    importInputRef.current?.click()
  }, [])

  const handleImportPrompts = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(event.target.files ?? [])
      event.target.value = ''

      await importPrompts(selectedFiles)
    },
    [importPrompts]
  )

  const handleImportDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open && isImportingPrompts) {
        cancelImportPrompts()
      }
    },
    [cancelImportPrompts, isImportingPrompts]
  )

  const handleTabSelect = useCallback((_: unknown, tabData: { value: unknown }) => {
    if (!isPromptTabValue(tabData.value)) {
      return
    }

    const tab = tabData.value
    setSelectedTab(tab)
    savePromptTab(tab)
  }, [])

  const pageHeaderActions = (
    <Tooltip content={t('prompts.importFromJson')} relationship="description" appearance="inverted">
      <Button
        appearance="subtle"
        icon={{ children: <AddRegular />, style: { color: 'inherit' } }}
        className={classes.addButton}
        onClick={handleOpenImportPicker}
        disabled={isImportingPrompts}>
        {t('addAction')}
      </Button>
    </Tooltip>
  )

  useEffect(() => {
    if (selectedTab === null && data) {
      setSelectedTab(resolveInitialPromptTab(data.favorites.length > 0))
    }
  }, [data, selectedTab])

  if (isError && !isFetching) {
    return (
      <div className={classes.container}>
        <PageHeader title={t('prompts.heading')} actions={pageHeaderActions} />
        <div className={classes.errorContainer}>
          <div className={classes.errorIconAndMessage}>
            <DismissCircle48Color aria-hidden="true" />
            <Body1Strong as="p" role="alert">
              {t('prompts.error.title')}
            </Body1Strong>
          </div>
          <Button appearance="primary" onClick={() => refetch()}>
            {t('prompts.error.tryAgain')}
          </Button>
        </div>
      </div>
    )
  }

  if (isLoading || selectedTab === null || (isFetching && isError)) {
    return (
      <div className={classes.container}>
        <PageHeader title={t('prompts.heading')} actions={pageHeaderActions} />
        <Spinner
          size="small"
          label={t('loading')}
          labelPosition="below"
          className={classes.spinner}
        />
      </div>
    )
  }

  return (
    <div className={classes.container}>
      <PageHeader title={t('prompts.heading')} actions={pageHeaderActions} />
      <div className={classes.tabsContainer}>
        <TabList
          selectedValue={selectedTab}
          onTabSelect={handleTabSelect}
          appearance="subtle-circular"
          size="medium"
          className={classes.tabList}>
          <Tab value={PromptTabValue.favorites}>{t('prompts.tabs.favorites')}</Tab>
          <Tab value={PromptTabValue.standard}>{t('prompts.tabs.standard')}</Tab>
          <Tab value={PromptTabValue.custom}>{t('prompts.tabs.custom')}</Tab>
        </TabList>
      </div>
      <div className={classes.tabContent}>
        {selectedTab === PromptTabValue.standard ? (
          <StandardPromptAccordion prompts={data?.standard ?? []} />
        ) : data?.[selectedTab].length === 0 ? (
          <EmptyPromptsContent variant={selectedTab} />
        ) : (
          data?.[selectedTab].map((prompt) => {
            const type = prompt.promptType === 'custom' ? 'custom' : 'standard'
            return (
              <PromptCard
                key={prompt.promptId}
                id={prompt.promptId}
                type={type}
                title={prompt.title}
                llmModel={prompt.llmModel}
                previewText={type === 'custom' ? prompt.userPrompt : prompt.description}
                promptToInsert={prompt.userPrompt}
                isFavorite={prompt.isFavorite}
                reviewMode={prompt.reviewMode}
                requiresDocumentComments={prompt.requiresDocumentComments}
                systemPrompt={prompt.systemPrompt}
                requiresFullDocument={prompt.requiresFullDocument}
              />
            )
          })
        )}
      </div>
      <input
        ref={importInputRef}
        type="file"
        multiple
        accept=".json,application/json"
        className={classes.hiddenInput}
        aria-label={t('prompts.importFromJson')}
        onChange={handleImportPrompts}
      />
      <Dialog
        title={t('dialog.importPromptsDialogTitle')}
        content={t('dialog.importPromptsDialogContent')}
        secondaryButtonText={t('cancel')}
        onOpenChange={handleImportDialogOpenChange}
        isDialogOpen={isImportingPrompts}
        isLoading={isImportingPrompts}
        loadingLabel={t('dialog.importPromptsDialogLoading')}
        keepSecondaryActionVisibleWhileLoading={true}
      />
    </div>
  )
}

export { Prompts }
